import { CreateTask_Body, CreateTask_Response, GetTaskManagerByUserId_Params, ITaskManager, ITaskManagerSection, UpdateTask_Body, UpdateTask_Params, UpdateTask_Response, createTask, getTaskManagerByUserId, updateTask } from '@/services/task-service';
import { AsyncThunkConfig } from '@/store';
import { createUID } from '@/utils/create-uid';
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

const initialState: TaskManagerSlice = {
  loading: false,
  data: null,
  board: {},
  sorted: [],
};

const taskManagerSlice = createSlice({
  name: 'taskManager',
  initialState: initialState,
  reducers: {
    updateTaskManagerDataGenericReducer: (state, action: PayloadAction<Partial<TaskManagerSlice>>) => {
      const { loading, board, data, sorted } = action.payload;
      state.loading = loading !== undefined ? loading : state.loading;
      state.board = board !== undefined ? board : state.board;
      state.data = data !== undefined ? data : state.data;
      state.sorted = sorted !== undefined ? sorted : state.sorted;
    },
    createSectionReducer: (state, action: PayloadAction<Omit<ITaskManagerSection, '_id'>>) => {
      const newSection = { ...action.payload, _id: createUID() };
      state.sorted = [...state.sorted, newSection];
      state.board = { ...state.board, [newSection._id]: newSection.tasks };
    },
    updateSectionReducer: (state, action: PayloadAction<ITaskManagerSection>) => {
      state.sorted = state.sorted.map((item) => (item._id === action.payload._id ? action.payload : item));
    },
    deleteSectionReducer: (state, action: PayloadAction<ITaskManagerSection['_id']>) => {
      state.sorted = state.sorted.filter((item) => item._id !== action.payload);
      delete state.board[action.payload];
    },
    // updateProductReducer: (state, action: PayloadAction<{ categoryId: ICategory['_id']; productData: IMenuProduct }>) => {
    //   state.board[action.payload.categoryId] = state.board[action.payload.categoryId].map((item) => (item._id === action.payload.productData._id ? action.payload.productData : item));
    //   if (state.menu) {
    //     state.canPublish = true;
    //     MenuBoardDraft.Set(state.menu._id, { board: state.board, sorted: state.sorted });
    //   }
    // },
  },
  extraReducers: (builder) => {
    builder.addCase(getTaskManagerByUserIdThunk.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getTaskManagerByUserIdThunk.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload === null) {
        state.data = null;
        state.board = {};
        state.sorted = [];
      } else {
        const { sections, ...rest } = action.payload;
        state.data = rest;
        state.sorted = sections;
        sections.forEach((item) => (state.board[item._id] = item.tasks));
      }
    });
    builder.addCase(createTaskThunk.fulfilled, (state, action) => {
      if (action.payload.taskData === null) return;
      const { sectionId, taskData } = action.payload;
      const { _id, assignedTo, attachement, comments, createdBy, data, description, priority, status, title, type } = taskData;
      state.board[action.payload.sectionId] = [...state.board[sectionId], { _id, assignedTo, attachement, description, priority, status, title, type, comments, createdBy, data }];
    });
    builder.addCase(updateTaskThunk.fulfilled, (state, action) => {
      if (action.payload.taskData === null) return;
      const { sectionId, taskData } = action.payload;
      state.board[sectionId] = state.board[sectionId].map((item) => (item._id === taskData._id ? taskData : item));
    });
  },
});

export const { updateTaskManagerDataGenericReducer, createSectionReducer, updateSectionReducer, deleteSectionReducer } = taskManagerSlice.actions;
export default taskManagerSlice.reducer;

export interface TaskManagerSlice {
  loading: boolean;
  data: Omit<ITaskManager, 'sections'> | null;
  board: Record<ITaskManagerSection['_id'], ITaskManagerSection['tasks']>;
  sorted: ITaskManager['sections'];
}

// ASYNC THUNKS
export const getTaskManagerByUserIdThunk = createAsyncThunk('taskManager/getTaskManagerByUserIdThunk', async (params: GetTaskManagerByUserId_Params) => {
  const response = await getTaskManagerByUserId(params);
  return response.data.data === null || response.data.data.length <= 0 ? null : response.data.data[0];
});

export const createTaskThunk = createAsyncThunk<{ sectionId: ITaskManagerSection['_id']; taskData: CreateTask_Response['data'] }, { sectionId: ITaskManagerSection['_id']; body: CreateTask_Body }, AsyncThunkConfig>('taskManager/createTaskThunk', async ({ sectionId, body }) => {
  const response = await createTask(body);
  return { sectionId, taskData: response.data.data };
});

export const updateTaskThunk = createAsyncThunk<{ sectionId: ITaskManagerSection['_id']; taskData: UpdateTask_Response['data'] }, { sectionId: ITaskManagerSection['_id']; params: UpdateTask_Params; body: UpdateTask_Body }, AsyncThunkConfig>('taskManager/updateTaskThunk', async ({ sectionId, params, body }) => {
  const response = await updateTask(params, body);
  return { sectionId, taskData: response.data.data };
});
